import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Bell, Search, Banknote, ShieldAlert, ExternalLink } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import type { VendorNotification } from "@shared/schema";
import { VendorPortalLinkApplier } from "./_shared";



// ── Notification Bell ────────────────────────────────────────────────────────
// Compact bell with unread badge + dropdown panel that surfaces money-event
// alerts (payout failed, dispute opened/closed, instant payout paid). Mirrors
// the host bell in app-layout.tsx but talks to /api/vendor-portal/...
// The dropdown shows the most recent few; the "View all" link in the footer
// jumps to the full Notifications tab built for Task #1258.
export function NotificationBell({ onViewAll, applyInternalLink }: { onViewAll?: () => void; applyInternalLink?: VendorPortalLinkApplier }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  const { data: notifications = [] } = useQuery<VendorNotification[]>({
    queryKey: ["/api/vendor-portal/notifications"],
    refetchInterval: 60_000,
  });
  const { data: countData } = useQuery<{ count: number }>({
    queryKey: ["/api/vendor-portal/notifications/count"],
    refetchInterval: 30_000,
  });
  const unreadCount = countData?.count ?? 0;

  const markRead = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => apiRequest("PATCH", `/api/vendor-portal/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/notifications/count"] });
    },
  });
  const markAllRead = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => apiRequest("PATCH", `/api/vendor-portal/notifications/read-all`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/notifications/count"] });
    },
  });

  const handleClick = (n: VendorNotification) => {
    if (!n.read) markRead.mutate(n.id);
    setOpen(false);
    if (!n.link) return;
    // Vendor-portal deep-links route through the local applier so tab/task
    // state updates even when the vendor is already inside the portal.
    if (applyInternalLink && applyInternalLink(n.link)) return;
    if (n.link.startsWith("/")) navigate(n.link);
    else window.location.href = n.link;
  };

  return (
    <div className="relative">
      <button
        type="button"
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid="button-vendor-notifications"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5 text-gray-600" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-xs font-medium flex items-center justify-center"
            data-testid="badge-vendor-notification-count"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 top-full mt-2 w-80 bg-white border rounded-lg shadow-xl z-50 max-h-[70vh] flex flex-col"
            data-testid="panel-vendor-notifications"
          >
            <div className="flex items-center justify-between p-3 border-b">
              <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    className="text-xs text-blue-600 hover:underline px-1"
                    onClick={() => markAllRead.mutate()}
                    data-testid="button-vendor-mark-all-read"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  className="p-1 hover:bg-gray-100 rounded"
                  onClick={() => setOpen(false)}
                  aria-label="Close notifications"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No notifications yet</p>
                </div>
              ) : (
                notifications.slice(0, 8).map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-3 py-2 border-b last:border-0 hover:bg-gray-50 transition-colors ${n.read ? "" : "bg-blue-50/40 dark:bg-blue-950/20"}`}
                    data-testid={`notification-vendor-${n.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-gray-900 leading-snug">{n.title}</p>
                      {!n.read && <span className="mt-1 h-2 w-2 rounded-full bg-blue-500 shrink-0" />}
                    </div>
                    {n.message && (
                      <p className="text-xs text-gray-600 mt-0.5 leading-snug">{n.message}</p>
                    )}
                    <p className="text-[10px] text-gray-400 mt-1">
                      {new Date(n.createdAt as unknown as string).toLocaleString()}
                    </p>
                  </button>
                ))
              )}
            </div>
            {onViewAll && (
              <div className="border-t p-2 text-center">
                <button
                  type="button"
                  onClick={() => { setOpen(false); onViewAll(); }}
                  className="text-xs text-blue-600 hover:underline font-medium"
                  data-testid="button-vendor-view-all-notifications"
                >
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}


// ── Notifications Tab ────────────────────────────────────────────────────────
// Full-page list of vendor notifications (Task #1258). Reads the same endpoint
// the bell uses but adds search, type/read filters, and a "load more" pager.
export type NotifTypeFilter = "all" | "payout" | "dispute" | "other";

export type NotifReadFilter = "all" | "unread" | "read";


export const NOTIF_PAGE_SIZE = 20;


export function notifCategory(type: string | null | undefined): "payout" | "dispute" | "other" {
  const t = (type || "").toLowerCase();
  if (t.includes("payout")) return "payout";
  if (t.includes("dispute")) return "dispute";
  return "other";
}


// Task #2196 — notifications of type `task_rejected` get a loud red treatment.
export function notifTypeBadge(type: string | null | undefined): { label: string; className: string; Icon: typeof Banknote } {
  const t = (type || "").toLowerCase();
  switch (t) {
    case "task_rejected":
      return { label: "Task rejected", className: "bg-red-100 text-red-700 border-red-300 font-semibold", Icon: ShieldAlert };
    case "payout_failed":
      return { label: "Payout failed", className: "bg-red-100 text-red-700 border-red-200", Icon: Banknote };
    case "payout_paid":
      return { label: "Payout sent", className: "bg-green-100 text-green-700 border-green-200", Icon: Banknote };
    case "dispute_opened":
      return { label: "Dispute opened", className: "bg-amber-100 text-amber-800 border-amber-200", Icon: ShieldAlert };
    case "dispute_closed":
      return { label: "Dispute closed", className: "bg-gray-100 text-gray-700 border-gray-200", Icon: ShieldAlert };
    default:
      return { label: type || "Notification", className: "bg-gray-100 text-gray-700 border-gray-200", Icon: Bell };
  }
}


export function NotificationsTab({ applyInternalLink }: { applyInternalLink?: VendorPortalLinkApplier }) {
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [typeFilter, setTypeFilter] = useState<NotifTypeFilter>("all");
  const [readFilter, setReadFilter] = useState<NotifReadFilter>("all");
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(NOTIF_PAGE_SIZE);

  const { data: notifications = [], isLoading } = useQuery<VendorNotification[]>({
    queryKey: ["/api/vendor-portal/notifications"],
    refetchInterval: 60_000,
  });

  const markRead = useMutation({
    meta: { silentSave: true },
    mutationFn: async (id: number) => apiRequest("PATCH", `/api/vendor-portal/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/notifications/count"] });
    },
  });
  const markAllRead = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => apiRequest("PATCH", `/api/vendor-portal/notifications/read-all`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/vendor-portal/notifications/count"] });
    },
  });

  const filtered = notifications.filter((n) => {
    if (typeFilter !== "all" && notifCategory(n.type) !== typeFilter) return false;
    if (readFilter === "unread" && n.read) return false;
    if (readFilter === "read" && !n.read) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const haystack = `${n.title} ${n.message ?? ""} ${n.type ?? ""}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const visible = filtered.slice(0, visibleCount);
  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleOpen = (n: VendorNotification) => {
    if (!n.read) markRead.mutate(n.id);
    if (!n.link) return;
    if (applyInternalLink && applyInternalLink(n.link)) return;
    if (n.link.startsWith("/")) navigate(n.link);
    else window.location.href = n.link;
  };

  return (
    <div className="space-y-4" data-testid="tab-content-notifications">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">Payment alerts</h3>
          <p className="text-xs text-gray-500">Payout and dispute updates from PadPulse and Stripe.</p>
        </div>
        {unreadCount > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => markAllRead.mutate()}
            disabled={markAllRead.isPending}
            data-testid="button-notifications-mark-all-read"
          >
            Mark all read
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search notifications…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setVisibleCount(NOTIF_PAGE_SIZE); }}
            className="pl-8"
            data-testid="input-notifications-search"
          />
        </div>
        <div className="grid grid-cols-2 gap-2" data-tour="notifications-filters">
          <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v as NotifTypeFilter); setVisibleCount(NOTIF_PAGE_SIZE); }}>
            <SelectTrigger data-testid="select-notifications-type">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="option-notif-type-all">All types</SelectItem>
              <SelectItem value="payout" data-testid="option-notif-type-payout">Payouts</SelectItem>
              <SelectItem value="dispute" data-testid="option-notif-type-dispute">Disputes</SelectItem>
              <SelectItem value="other" data-testid="option-notif-type-other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Select value={readFilter} onValueChange={(v) => { setReadFilter(v as NotifReadFilter); setVisibleCount(NOTIF_PAGE_SIZE); }}>
            <SelectTrigger data-testid="select-notifications-read">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="option-notif-read-all">All</SelectItem>
              <SelectItem value="unread" data-testid="option-notif-read-unread">Unread only</SelectItem>
              <SelectItem value="read" data-testid="option-notif-read-read">Read only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div data-tour="notifications-list">
      {isLoading ? (
        <p className="text-sm text-gray-400 text-center py-8" data-testid="text-notifications-loading">Loading notifications…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400" data-testid="empty-notifications">
          <Bell className="h-10 w-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">
            {notifications.length === 0
              ? "No notifications yet. Payout and dispute alerts will appear here."
              : "No notifications match your filters."}
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500" data-testid="text-notifications-count">
            Showing {visible.length} of {filtered.length}
          </p>
          <div className="space-y-2">
            {visible.map((n) => {
              const badge = notifTypeBadge(n.type);
              const Icon = badge.Icon;
              return (
                <Card
                  key={n.id}
                  className={`transition-colors ${n.read ? "" : "border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-950/20"}`}
                  data-testid={`card-notification-${n.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${badge.className}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-gray-900 leading-snug" data-testid={`text-notification-title-${n.id}`}>
                            {n.title}
                          </p>
                          {!n.read && (
                            <Badge
                              className="bg-blue-500 hover:bg-blue-500 text-white text-[10px] shrink-0"
                              data-testid={`badge-notification-unread-${n.id}`}
                            >
                              New
                            </Badge>
                          )}
                        </div>
                        {n.message && (
                          <p className="text-xs text-gray-600 mt-0.5 leading-snug" data-testid={`text-notification-message-${n.id}`}>
                            {n.message}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${badge.className}`}
                            data-testid={`badge-notification-type-${n.id}`}
                          >
                            {badge.label}
                          </span>
                          <span className="text-[10px] text-gray-400" data-testid={`text-notification-time-${n.id}`}>
                            {new Date(n.createdAt as unknown as string).toLocaleString()}
                          </span>
                          <div className="ml-auto flex items-center gap-2">
                            {!n.read && (
                              <button
                                type="button"
                                className="text-[11px] text-blue-600 hover:underline"
                                onClick={() => markRead.mutate(n.id)}
                                data-testid={`button-notification-mark-read-${n.id}`}
                              >
                                Mark read
                              </button>
                            )}
                            {n.link && (
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
                                onClick={() => handleOpen(n)}
                                data-testid={`button-notification-open-${n.id}`}
                              >
                                Open <ExternalLink className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {visible.length < filtered.length && (
            <div className="text-center pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setVisibleCount((c) => c + NOTIF_PAGE_SIZE)}
                data-testid="button-notifications-load-more"
              >
                Load more
              </Button>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
}
